/**
 * Safe HTTP utility with automatic JSON parsing and Zod validation
 * Eliminates "unknown" type issues across the codebase
 */

import { z } from "zod";

/**
 * Fetch and parse JSON with optional Zod schema validation
 * @param input - Request URL or Request object
 * @param init - Fetch options
 * @param schema - Optional Zod schema for runtime validation
 * @returns Parsed and validated data
 */
export async function getJson<TSchema extends z.ZodTypeAny>(
  input: RequestInfo,
  init: RequestInit | undefined,
  schema: TSchema
): Promise<z.infer<TSchema>>;
export async function getJson<T = unknown>(
  input: RequestInfo,
  init?: RequestInit
): Promise<T>;
export async function getJson(
  input: RequestInfo,
  init?: RequestInit,
  schema?: z.ZodTypeAny
): Promise<unknown> {
  const res = await fetch(input, init);
  
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const url = typeof input === 'string' ? input : input.url;
    throw new Error(`[${res.status}] ${url} ${text.slice(0, 200)}`);
  }
  
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  
  if (schema) {
    return schema.parse(data);
  }
  
  return data;
}

/**
 * POST with JSON body and optional Zod validation
 */
export async function postJson<T>(
  url: string,
  body: unknown,
  schema?: z.ZodTypeAny
): Promise<T> {
  const init: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };

  return schema
    ? (getJson(url, init, schema) as Promise<T>)
    : getJson<T>(url, init);
}

/**
 * PUT with JSON body and optional Zod validation
 */
export async function putJson<T>(
  url: string,
  body: unknown,
  schema?: z.ZodTypeAny
): Promise<T> {
  const init: RequestInit = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };

  return schema
    ? (getJson(url, init, schema) as Promise<T>)
    : getJson<T>(url, init);
}

/**
 * DELETE with optional Zod validation
 */
export async function deleteJson<T>(
  url: string,
  schema?: z.ZodTypeAny
): Promise<T> {
  const init: RequestInit = {
    method: "DELETE",
  };

  return schema
    ? (getJson(url, init, schema) as Promise<T>)
    : getJson<T>(url, init);
}
